// Pure builders for the flyover share wizard's config, kept out of RecordingMap.vue so the
// deterministic unit conversions (elapsed seconds → "Xh Ym", metres → km/ft via the measurement
// preference) are unit-testable in isolation.

import { i18n } from '../../i18n';
import { privacyFromDataset, recordingPagePrivacy, type PageLinkPrivacy } from './pageLinkPrivacy';

export interface ShareConfigInput {
  id?: string | number | null;
  shareableUrl?: string | null;
  title?: string | null;
  distanceMeters?: number | null;
  elevationGainMeters?: number | null;
  elapsedSeconds?: number | null;
  thumbnailUrl?: string | null;
  public?: boolean | null;
  publicUser?: { firebaseUserId?: string | null } | null;
}

// Just the slice of MeasurementPrefUtility we use, so tests can pass a fake formatter.
export interface UnitFormatter {
  bigUnitFormatter(km: number): string;
  smallUnitFormatter(metres: number): string;
}

export interface RecordingShareConfig {
  recordingId: string;
  shareUrl: string;
  title: string;
  distance: string;
  elevation: string;
  duration: string;
  thumbnailUrl: string;
  pagePrivacy: PageLinkPrivacy;
}

/** Elapsed seconds → "2h 15m" (or "15m" under an hour). Blank for missing/invalid/zero. */
export function formatFlyoverDuration(seconds?: number | null): string {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return '';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h > 0
    ? i18n.global.t('flyover_share.recording_share_config.hours_minutes', { hours: h, minutes: m })
    : i18n.global.t('flyover_share.recording_share_config.minutes', { minutes: m });
}

/**
 * Build the flyover share wizard's config from a loaded recording, mirroring the recordings/show
 * page's ".generate-3d-flyover" data-* attributes so the wizard behaves identically from the map.
 * The measurement formatter is injected so this stays a pure function. `viewerFirebaseUserId` is the
 * signed-in user's, compared with the recording's owner to decide whether a private recording's
 * owner is the one sharing it (and so could be asked to make it public).
 */
export function buildRecordingShareConfig(
  recording: ShareConfigInput | null | undefined,
  pref: UnitFormatter,
  viewerFirebaseUserId: string | null = null,
): RecordingShareConfig {
  const r = recording || {};
  const owner = (r.publicUser?.firebaseUserId ?? '').trim();
  return {
    recordingId: r.id != null ? String(r.id) : '',
    shareUrl: r.shareableUrl || '',
    title: r.title || i18n.global.t('shared.recording'),
    distance: r.distanceMeters != null ? pref.bigUnitFormatter(r.distanceMeters / 1000) : '',
    elevation: r.elevationGainMeters != null ? pref.smallUnitFormatter(r.elevationGainMeters) : '',
    duration: formatFlyoverDuration(r.elapsedSeconds),
    thumbnailUrl: r.thumbnailUrl || '',
    pagePrivacy: recordingPagePrivacy(r, !!owner && owner === (viewerFirebaseUserId ?? '').trim()),
  };
}

/** The page globals recordings/show sets alongside the share element. */
export interface RecordingPageGlobals {
  recordingId?: string | number | null;
  recordingShareUrl?: string | null;
  recordingName?: string | null;
}

/**
 * The wizard config the recording page's "Generate 3D flyover" item carries in its data-* attributes
 * (recordings/show.html.erb). Read by that item and by the 3D preview's "Share video" when it was
 * opened without a config of its own, so both agree — privacy included — on what the share carries.
 */
export function recordingShareConfigFromDataset(
  ds: Record<string, string | undefined> | null | undefined,
  globals: RecordingPageGlobals = {},
): RecordingShareConfig {
  const d = ds || {};
  const recordingId = d.recordingId || String(globals.recordingId || '');
  return {
    recordingId,
    shareUrl: d.shareUrl || globals.recordingShareUrl || '',
    title: d.title || globals.recordingName || '',
    distance: d.distance || '',
    elevation: d.elevation || '',
    duration: d.duration || '',
    thumbnailUrl: d.thumbnailUrl || '',
    pagePrivacy: privacyFromDataset({ subjectId: recordingId, ...d }, 'recording'),
  };
}
